import { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import { cashPayment, formatMoney, formatPaidDate, isNotSetUp, notSetUpMessage, parseAmount, requestPayments, safeReceiptUrl, settled, statusLabel, stripeHostedUrl } from '../payments/api.js';

const newRequestId = () => globalThis.crypto.randomUUID();
// Big, readable controls: at least 48px tall, 1rem text.
const bigButton = { minHeight: 48, padding: '0 20px', fontSize: '1rem' };
const textButton = { minHeight: 48, padding: '0 8px', fontSize: '1rem', background: 'none', border: 'none', color: 'var(--blue)', textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 };
const statusLine = { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '1.1rem', lineHeight: 1.45, margin: '0 0 14px' };
const statusIcons = { paid: '✓', pending: '◷', unpaid: '○', partially_refunded: '↺', refunded: '↺', disputed: '!' };

/** Invoice and payment for one agreed job. Props: { work, onChanged }. Every status shown comes from the server. */
export function WorkInvoices({ work, openCheckout = (url) => window.location.assign(url) }) {
  if (!work?.id) return null;
  return <InvoicePanel key={work.id} work={work} openCheckout={openCheckout} />;
}

function InvoicePanel({ work, openCheckout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [notSetUp, setNotSetUp] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [refund, setRefund] = useState(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  useEffect(() => {
    let current = true;
    requestPayments(supabase, 'work-invoices', { workId: work.id }).then((value) => {
      if (!current) return;
      setData(value); setNotSetUp(!value.configured); setError('');
    }, (cause) => {
      if (!current) return;
      if (isNotSetUp(cause)) setNotSetUp(true); else setError(cause.message);
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [work.id, version]);

  const replace = (invoice) => setData((current) => current && { ...current, invoices: [invoice, ...current.invoices.filter((item) => item.id !== invoice.id)] });
  async function run(action) {
    if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try { await action(); }
    catch (cause) { if (alive.current) { if (isNotSetUp(cause)) setNotSetUp(true); else setError(cause.message); } }
    finally { if (alive.current) setBusy(false); }
  }
  const issue = () => run(async () => {
    const price = work.amount_minor && work.currency ? formatMoney(work.amount_minor, work.currency) : 'the agreed price';
    if (!window.confirm(`Send the customer an invoice for ${price}, the agreed price for this job?`)) return;
    const { invoice } = await requestPayments(supabase, 'issue-work-invoice', { workId: work.id });
    if (alive.current) { replace(invoice); setNotice('Invoice sent to the customer.'); }
  });
  const pay = (invoice) => run(async () => {
    const { url } = await requestPayments(supabase, 'create-job-payment', { invoiceId: invoice.id, channel: 'web' });
    // Stripe's hosted checkout. Returning to Resident Service 360 never marks the invoice paid by itself.
    openCheckout(stripeHostedUrl(url, ['checkout.stripe.com']));
  });
  const cash = (invoice, action) => run(async () => {
    const price = formatMoney(invoice.amountMinor, invoice.currency);
    const supplierName = invoice.sellerLegalName || 'the supplier';
    const ask = {
      offer: `Pay in cash?\n\nTell ${supplierName} you will pay ${price} in cash when the job is done. They will confirm here when they have it.`,
      confirm: `Did the customer pay you ${price} in cash?`,
      decline: `Ask them to pay by card?\n\nThe customer will be asked to pay ${price} by card instead of cash.`,
    }[action];
    if (ask && !window.confirm(ask)) return;
    const next = await cashPayment(supabase, invoice.id, action);
    if (!alive.current) return;
    replace(next);
    setNotice({
      offer: `We've told ${supplierName} you'll pay in cash.`,
      withdraw: "You're no longer paying in cash. You can pay by card instead.",
      confirm: 'Cash payment recorded. Thank you.',
      decline: "We've asked the customer to pay by card.",
    }[action]);
  });
  const check = (invoice) => run(async () => {
    const { invoice: next } = await requestPayments(supabase, 'refresh-job-payment', { invoiceId: invoice.id });
    if (!alive.current) return;
    replace(next);
    if (!settled(next.status)) setNotice('Stripe has not confirmed this payment yet. Check again in a moment.');
  });
  const sendRefund = (invoice) => run(async () => {
    const remaining = invoice.amountMinor - invoice.refundedAmountMinor;
    const minor = parseAmount(refund.amount);
    if (!minor || minor > remaining) { setError(`Enter an amount up to ${formatMoney(remaining, invoice.currency)}.`); return; }
    if (!window.confirm(`Refund ${formatMoney(minor, invoice.currency)} to the customer's card? This can't be undone.`)) return;
    // One request id per amount, so a retry after a network failure never refunds twice.
    const requestId = refund.request?.amount === minor ? refund.request.id : newRequestId();
    setRefund((current) => ({ ...current, request: { amount: minor, id: requestId } }));
    const { invoice: next } = await requestPayments(supabase, 'refund-job-payment', { invoiceId: invoice.id, amountMinor: minor, requestId });
    if (alive.current) { replace(next); setRefund(null); setNotice('Refund sent. Stripe returns it to the customer within a few days.'); }
  });

  const invoice = data?.invoices?.[0] || null;
  const viewer = data?.viewer;
  let content;
  if (loading && !data) content = <p>Loading invoice…</p>;
  else if (!data) content = notSetUp ? <p className="cloud-notice">{notSetUpMessage}</p> : null;
  else if (!invoice) {
    if (notSetUp) content = <p className="cloud-notice">{notSetUpMessage}</p>;
    else if (viewer?.canIssue) content = viewer.priceAgreed
      ? <><p>Send the customer an invoice for the agreed price{work.amount_minor && work.currency ? ` of ${formatMoney(work.amount_minor, work.currency)}` : ''}.</p>
        <button className="button button-blue" disabled={busy} onClick={() => void issue()}>Issue invoice</button></>
      : <p>This job has no agreed price, so an invoice can&apos;t be issued.</p>;
    else if (viewer?.side === 'supplier') content = <p>No invoice yet. A company owner or admin can issue one.</p>;
    else content = <p>The supplier hasn&apos;t sent an invoice yet.</p>;
  } else {
    const receipt = safeReceiptUrl(invoice.receiptUrl);
    const price = formatMoney(invoice.amountMinor, invoice.currency);
    const supplierName = invoice.sellerLegalName || 'The supplier';
    const isCustomer = viewer?.side === 'customer';
    const paidInCash = invoice.paymentMethod === 'cash' && settled(invoice.status);
    const cashOffered = Boolean(invoice.cashOfferedAt) && invoice.status === 'unpaid';
    const unpaid = invoice.status === 'unpaid';
    const canPay = viewer?.canPay && !settled(invoice.status) && !notSetUp;
    const refundable = viewer?.canRefund && invoice.paymentMethod !== 'cash' && ['paid', 'partially_refunded'].includes(invoice.status) && !notSetUp;
    const paidDate = formatPaidDate(invoice.paidAt);
    const statusText = paidInCash ? `Paid ${price} in cash${paidDate ? ` on ${paidDate}` : ''}`
      : cashOffered ? (isCustomer ? `You're paying ${price} in cash. ${supplierName} will confirm when they get it.` : `The customer will pay ${price} in cash.`)
        : statusLabel(invoice.status, invoice);
    const offerCash = isCustomer && unpaid && !cashOffered && viewer?.canOfferCash;
    const withdrawCash = isCustomer && cashOffered && viewer?.canWithdrawCash;
    const confirmCash = !isCustomer && unpaid && viewer?.canConfirmCash;
    const declineCash = !isCustomer && cashOffered && viewer?.canDeclineCash;
    content = <>
      <p style={statusLine} aria-label={`Invoice status: ${statusText}`}>
        <span aria-hidden="true">{cashOffered ? '◷' : statusIcons[invoice.status] || '○'}</span><strong>{statusText}</strong>
      </p>
      <dl className="invoice-summary">
        <dt>From</dt><dd>{invoice.sellerLegalName}</dd>
        <dt>Address</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{invoice.sellerAddress}</dd>
        {invoice.sellerCompanyNumber && <><dt>Company number</dt><dd>{invoice.sellerCompanyNumber}</dd></>}
        <dt>For</dt><dd>{invoice.title}</dd>
        <dt>Amount</dt><dd>{formatMoney(invoice.amountMinor, invoice.currency)}</dd>
        {invoice.refundedAmountMinor > 0 && <><dt>Refunded</dt><dd>{formatMoney(invoice.refundedAmountMinor, invoice.currency)}</dd></>}
        <dt>Issued</dt><dd>{new Date(invoice.issuedAt).toLocaleDateString('en-GB')}</dd>
      </dl>
      {invoice.status === 'disputed' && <p className="cloud-notice">The customer&apos;s bank is reviewing this payment. Stripe will contact the supplier.</p>}
      {notSetUp && <p className="cloud-notice">{notSetUpMessage}</p>}
      <div className="coord-inline-actions">
        {canPay && (data.webCheckout
          ? <button className={cashOffered ? 'button button-secondary' : 'button button-blue'} style={bigButton} disabled={busy} onClick={() => void pay(invoice)}>
            {cashOffered ? 'Pay by card instead' : `Pay ${price} by card`}</button>
          : <p>Pay this invoice by card in the Resident Service 360 app.</p>)}
        {confirmCash && <button className={cashOffered ? 'button button-blue' : undefined} style={cashOffered ? bigButton : textButton} disabled={busy}
          aria-description={`Confirm the customer paid you ${price} in cash`} onClick={() => void cash(invoice, 'confirm')}>
          {cashOffered ? "I've been paid in cash" : 'Record a cash payment'}</button>}
        {invoice.status === 'pending' && <button className="button button-secondary" disabled={busy} onClick={() => void check(invoice)}>Check payment status</button>}
        {receipt && <a className="button button-secondary" href={receipt} target="_blank" rel="noreferrer noopener">View receipt</a>}
        {refundable && !refund && <button className="button button-secondary" disabled={busy} onClick={() => setRefund({ amount: ((invoice.amountMinor - invoice.refundedAmountMinor) / 100).toFixed(2), request: null })}>Refund customer</button>}
      </div>
      {(offerCash || withdrawCash || declineCash) && <div className="coord-inline-actions">
        {offerCash && <button type="button" style={textButton} disabled={busy} onClick={() => void cash(invoice, 'offer')}>Pay in cash instead</button>}
        {withdrawCash && <button type="button" style={textButton} disabled={busy} onClick={() => void cash(invoice, 'withdraw')}>I don&apos;t want to pay cash</button>}
        {declineCash && <button type="button" style={textButton} disabled={busy} onClick={() => void cash(invoice, 'decline')}>Ask for card instead</button>}
      </div>}
      {isCustomer && cashOffered && <p style={{ fontSize: '1rem' }}>Ask for a receipt when you pay.</p>}
      {paidInCash && !isCustomer && <p className="cloud-notice" style={{ fontSize: '1rem' }}>Cash refunds are done in person.</p>}
      {refundable && refund && <form className="coord-form" onSubmit={(event) => { event.preventDefault(); void sendRefund(invoice); }}>
        <label>Refund amount<input inputMode="decimal" value={refund.amount} disabled={busy} onChange={(event) => { const amount = event.target.value; setRefund((current) => ({ ...current, amount })); }} /></label>
        <button className="button button-blue" disabled={busy}>Send refund</button>
        <button className="button button-secondary" type="button" disabled={busy} onClick={() => setRefund(null)}>Not now</button>
      </form>}
    </>;
  }
  return <section className="account-content-panel" aria-labelledby={`invoice-${work.id}`}>
    <h2 id={`invoice-${work.id}`}>Invoice and payment</h2>
    {content}
    {notice && <p role="status">{notice}</p>}
    {error && <div role="alert"><p>{error}</p>{!data && <button className="button button-secondary" onClick={() => { setLoading(true); setVersion((value) => value + 1); }}>Try again</button>}</div>}
  </section>;
}
